<template>
  <div :class="$style.blockPayment">
    <div v-for="payment in paymentsData" :key="payment.id" :class="$style.blockPaymentItem">
      <PaymentCard
        :payment="payment"
        :card-data="paymentCardData"
        @cardClick="() => openDeposit(payment.paymentMethodLogoId, payment.shouldOpenByCryptoModal)"
      />
    </div>
  </div>
</template>
<script lang="ts" setup>
import { PaymentCard } from '~/components/lib/payments';
import { useStoreGtm } from '~/stores/abstract/gtm';
import { useStoreModals } from '~/stores/abstract/modals';
import { useStoreRoot } from '~/stores/abstract/root';
import { useStoreMenuStatuses } from '~/stores/client/menuStatuses';
import { useStoreSsPlayer } from '~/stores/softswiss/player';
import { useStoreCurrencyMenu } from '~/stores/strapi/currencyMenu';
import { EModalName } from '~/types/modals';
import type { ComponentInfoPageBlocksPaymentsBlock } from '~/types/infoPages';
import type { IPaymentData } from '~/types/payments';

const props = defineProps({
  data: {
    type: Object as PropType<ComponentInfoPageBlocksPaymentsBlock>,
    required: true,
  },
  payments: {
    type: Array as PropType<IPaymentData[]>,
    required: true,
  },
  deposit: {
    type: Boolean,
    required: true,
  },
  gtmLabel: {
    type: String,
    default: () => '',
  },
  gtmCategory: {
    type: String,
    default: () => '',
  },
  gtmAction: {
    type: String,
    default: () => '',
  },
});

const storeModals = useStoreModals();
const storeMenuStatuses = useStoreMenuStatuses();
const storeGtm = useStoreGtm();
const storeRoot = useStoreRoot();
const storeSsPlayer = useStoreSsPlayer();
const storeCurrencyMenu = useStoreCurrencyMenu();

/**
 * Function for getting data for Info Pages blocks and
 * getting data for Payments Data from strapiand glue them
 */
const paymentsData = computed(() => {
  const data = props.data as { [key: string]: any };

  return props.payments.map((p) => ({
    ...p,
    paymentType:
      data['paymentType' + p.paymentType.slice(0, 1).toUpperCase() + p.paymentType.slice(1)] ||
      p.paymentType,
    paymentProcessingTime:
      data[
        'paymentProcessingTime' +
          p.paymentProcessingTime?.slice(0, 1).toUpperCase() +
          p.paymentProcessingTime?.slice(1)
      ] || p.paymentProcessingTime,
  }));
});

const paymentCardData = computed(() => {
  return {
    tableTypeTitle: props.data.tableTypeTitle,
    tableFeeTitle: props.data.tableFeeTitle,
    tableProcessingTimeTitle: props.data.tableProcessingTimeTitle,
    tableLimitsTitle: props.data.tableLimitsTitle,
    paymentLimitsMin: props.data.paymentLimitsMin,
    paymentLimitsMax: props.data.paymentLimitsMax,
  };
});

const openDeposit = (paymentMethodLogoId: any, shouldOpenByCryptoModal?: boolean) => {
  if (!storeSsPlayer.isPlayerAuthorised) {
    storeModals.openLoginOrSignupModal();
  } else {
    // if (storeModals.activeModal) {
    //   storeModals.closeModal(storeModals.activeModal.name as EModalName);
    // }

    if (
      shouldOpenByCryptoModal === true &&
      storeCurrencyMenu.isCryptoCurency(storeRoot.userCurrency)
    ) {
      storeModals.openModal(EModalName.buy_crypto);
    } else if (storeRoot.isEstonianLicense && !storeSsPlayer.isDepositReady) {
      storeModals.openModal(EModalName.profile);
    } else {
      const modalName = props.deposit ? EModalName.deposit : EModalName.withdraw;
      const params = { ...(paymentMethodLogoId ? { paymentMethodLogoId } : {}) };
      storeModals.openModal(modalName, params);
    }

    storeMenuStatuses.toggleNotificationsMenu(false);
    storeMenuStatuses.toggleSidebarMenu(false);

    if (props.gtmAction && props.gtmCategory) {
      storeGtm.push({
        vCategory: props.gtmCategory,
        vAction: props.gtmAction,
        vLabel: props.gtmLabel || undefined,
      });
    }
  }
};
</script>

<style lang="scss" module src="./paymentBlockTable.scss"></style>
